import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  leadStatsRefreshDtoSchema,
  leadStatsSettingsDtoSchema,
  venueLeadStatsSettingsDtoSchema,
  type LeadStatsRefreshDto,
  type LeadStatsSettingsDto,
  type VenueLeadStatsSettingsDto,
} from '@lib/dto/lead-stats.dto'
import {
  companyLeadStatsDtoSchema,
  venueLeadStatsDtoSchema,
  type CompanyLeadStatsDto,
  type VenueLeadStatsDto,
} from '@lib/dto/lead-stats-numbers.dto'
import type { LeadStatsQuery, PutLeadStatsSettingsInput, PutVenueLeadStatsAdjustmentsInput } from '@lib/input/lead-stats.input'
import type { ILeadStatsService } from '@/types/LeadStatsService.types'

/** Lead stats from GoHighLevel (docs/gohighlevel-stats.spec.md). */
class LeadStatsServiceImpl implements ILeadStatsService {
  async settings(): Promise<LeadStatsSettingsDto> {
    try {
      return await api.get('/lead-stats/settings', leadStatsSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the lead stats settings', {}, error)
      throw error
    }
  }

  async saveSettings(input: PutLeadStatsSettingsInput): Promise<LeadStatsSettingsDto> {
    try {
      return await api.put('/lead-stats/settings', input, leadStatsSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the lead stats settings', {}, error)
      throw error
    }
  }

  async venueSettings(venueId: string): Promise<VenueLeadStatsSettingsDto> {
    try {
      return await api.get(`/venues/${venueId}/lead-stats/settings`, venueLeadStatsSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s lead stats settings', { venueId }, error)
      throw error
    }
  }

  async saveVenueSettings(venueId: string, input: PutVenueLeadStatsAdjustmentsInput): Promise<VenueLeadStatsSettingsDto> {
    try {
      return await api.put(`/venues/${venueId}/lead-stats/settings`, input, venueLeadStatsSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the venue’s lead stats settings', { venueId }, error)
      throw error
    }
  }

  async refresh(venueId: string): Promise<LeadStatsRefreshDto> {
    try {
      return await api.post(`/venues/${venueId}/lead-stats/refresh`, {}, leadStatsRefreshDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update the venue’s lead stats', { venueId }, error)
      throw error
    }
  }

  async venue(venueId: string, query: LeadStatsQuery): Promise<VenueLeadStatsDto> {
    try {
      return await api.get(`/venues/${venueId}/lead-stats${searchOf(query)}`, venueLeadStatsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s lead stats', { venueId }, error)
      throw error
    }
  }

  async summary(query: LeadStatsQuery): Promise<CompanyLeadStatsDto> {
    try {
      return await api.get(`/lead-stats/summary${searchOf(query)}`, companyLeadStatsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load every venue’s lead stats', {}, error)
      throw error
    }
  }
}

/** A query as a search string: `?view=activity`, or nothing. */
function searchOf(query: LeadStatsQuery): string {
  const params = new URLSearchParams()
  if (query.pipeline !== undefined) params.set('pipeline', query.pipeline)
  if (query.view !== undefined) params.set('view', query.view)
  if (query.period !== undefined) params.set('period', query.period)
  const search = params.toString()
  return search === '' ? '' : `?${search}`
}

export const leadStatsService: ILeadStatsService = new LeadStatsServiceImpl()
